import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildColorFieldDefinitions } from "containers/theme-setter/definitions";
import { useThemeSetter } from "containers/theme-setter/hooks/useThemeSetter";
import { uploadThemeAsset } from "service";
import {
  clearThemePreviewConfig,
  saveThemePreviewConfig,
} from "utils/theme-preview-storage";
import { ThemeSetterLeftPanel } from "containers/theme-setter/components/ThemeSetterLeftPanel";
import { ThemeSetterRightPanel } from "containers/theme-setter/components/ThemeSetterRightPanel";
import type { ThemeConfig } from "containers/theme-setter/types";
import { cloneDeep } from "containers/theme-setter/utils";

const MAX_ASSET_BYTES = 5 * 1024 * 1024;

type PendingLandingAsset = {
  file: File;
  previewUrl: string;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Unable to read file"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });

const setConfigValueAtPath = (
  config: ThemeConfig,
  path: string,
  value: string
) => {
  const segments = path.split(".");
  let cursor: Record<string, unknown> = config as Record<string, unknown>;
  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      cursor[segment] = value;
      return;
    }
    if (!cursor[segment] || typeof cursor[segment] !== "object") {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  });
};

export const ThemeSetter = () => {
  const {
    themeConfig,
    context,
    normalizedCustomer,
    showPlaceholder,
    loading,
    saveError,
    saveStatus,
    fetchError,
    isDirty,
    colors,
    fonts,
    components,
    landingFieldDefinitions,
    textVariants,
    handleColorChange,
    handleFontChange,
    handleComponentChange,
    handleLandingChange,
    handleTextVariantChange,
    handleReset,
    handleSave,
  } = useThemeSetter();
  const [processingLandingFields, setProcessingLandingFields] = useState<
    Record<string, boolean>
  >({});
  const [pendingLandingAssets, setPendingLandingAssets] = useState<
    Record<string, PendingLandingAsset>
  >({});
  const [savingPendingAssets, setSavingPendingAssets] = useState(false);
  const [previewRevision, setPreviewRevision] = useState(0);
  const [previewPath, setPreviewPath] = useState("/");
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const handleLandingAssetSelect = useCallback(
    async (fieldPath: string, file: File) => {
      if (file.size > MAX_ASSET_BYTES) {
        window.alert("Please choose a file smaller than 5MB.");
        return;
      }
      setProcessingLandingFields((prev) => ({ ...prev, [fieldPath]: true }));
      try {
        const previewUrl = await readFileAsDataUrl(file);
        setPendingLandingAssets((prev) => ({
          ...prev,
          [fieldPath]: { file, previewUrl },
        }));
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to read the selected asset.";
        console.error("Asset preview failed", error);
        window.alert(message);
      } finally {
        setProcessingLandingFields((prev) => ({
          ...prev,
          [fieldPath]: false,
        }));
      }
    },
    []
  );

  const handleLandingAssetClear = useCallback((fieldPath: string) => {
    setPendingLandingAssets((prev) => {
      if (!(fieldPath in prev)) return prev;
      const next = { ...prev };
      delete next[fieldPath];
      return next;
    });
  }, []);

  const previewConfig = useMemo(() => {
    if (!themeConfig) return null;
    const pendingEntries = Object.entries(pendingLandingAssets);
    if (pendingEntries.length === 0) {
      return themeConfig;
    }
    const nextConfig = cloneDeep(themeConfig) as ThemeConfig;
    pendingEntries.forEach(([path, pending]) => {
      setConfigValueAtPath(nextConfig, path, pending.previewUrl);
    });
    return nextConfig;
  }, [pendingLandingAssets, themeConfig]);

  const colorFieldDefinitions = useMemo(
    () => buildColorFieldDefinitions(colors),
    [colors]
  );
  const standardColors = colorFieldDefinitions.filter(
    (definition) => definition.group === "standard"
  );
  const cautionColors = colorFieldDefinitions.filter(
    (definition) => definition.group === "caution"
  );

  useEffect(() => {
    if (!previewConfig || showPlaceholder) {
      clearThemePreviewConfig();
      setPreviewRevision((prev) => prev + 1);
      setPreviewPath("/");
      return;
    }
    saveThemePreviewConfig(normalizedCustomer, previewConfig);
    setPreviewRevision((prev) => prev + 1);
  }, [normalizedCustomer, previewConfig, showPlaceholder]);

  const handlePreviewLoad = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;
    try {
      const { pathname, search, hash } = iframeWindow.location;
      const combined = `${pathname}${search}${hash}`;
      setPreviewPath((prev) => {
        if (!combined || combined === prev) {
          return prev;
        }
        return combined;
      });
    } catch {}
  }, []);

  const previewUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const basePath = previewPath.startsWith("/")
      ? previewPath
      : `/${previewPath}`;
    const base = new URL(basePath, window.location.origin);
    const params = new URLSearchParams(base.search);
    params.set("theme-preview", "1");
    if (normalizedCustomer) {
      params.set("customer", normalizedCustomer);
    } else {
      params.delete("customer");
    }
    params.set("rev", String(previewRevision));
    base.search = params.toString();
    return base.toString();
  }, [normalizedCustomer, previewPath, previewRevision]);

  const handleSaveAll = useCallback(async () => {
    if (!themeConfig) return;
    const pendingEntries = Object.entries(pendingLandingAssets);
    if (pendingEntries.length === 0) {
      await handleSave();
      return;
    }
    setSavingPendingAssets(true);
    const customerSlug = normalizedCustomer || "default";
    const nextConfig = cloneDeep(themeConfig) as ThemeConfig;
    try {
      for (const [path, pending] of pendingEntries) {
        try {
          const response = await uploadThemeAsset(customerSlug, pending.file);
          setConfigValueAtPath(nextConfig, path, response.path);
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : "Unable to upload the selected asset.";
          console.error("Asset upload failed", error);
          throw new Error(`Failed to upload "${path}": ${message}`);
        }
      }
      await handleSave(nextConfig);
      setPendingLandingAssets({});
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to save configuration.";
      window.alert(message);
    } finally {
      setSavingPendingAssets(false);
    }
  }, [handleSave, normalizedCustomer, pendingLandingAssets, themeConfig]);

  const handleResetAll = useCallback(() => {
    setPendingLandingAssets({});
    setProcessingLandingFields({});
    handleReset();
  }, [handleReset]);

  useEffect(() => {
    setPendingLandingAssets({});
    setProcessingLandingFields({});
  }, [normalizedCustomer]);

  return (
    <div className="bg-gray-900 text-white px-4 lg:px-12 py-10 min-h-screen lg:h-screen lg:overflow-hidden">
      <div className="mx-auto h-full">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-stretch lg:justify-between h-full">
          <ThemeSetterLeftPanel
            normalizedCustomer={normalizedCustomer}
            showPlaceholder={showPlaceholder}
            fetchError={fetchError}
            context={context}
            themeConfig={themeConfig}
            standardColors={standardColors}
            cautionColors={cautionColors}
            fonts={fonts}
            components={components}
            landingFieldDefinitions={landingFieldDefinitions}
            textVariants={textVariants}
            pendingLandingAssets={pendingLandingAssets}
            processingLandingFields={processingLandingFields}
            handleColorChange={handleColorChange}
            handleFontChange={handleFontChange}
            handleComponentChange={handleComponentChange}
            handleLandingChange={handleLandingChange}
            handleLandingAssetSelect={handleLandingAssetSelect}
            handleLandingAssetClear={handleLandingAssetClear}
            handleTextVariantChange={handleTextVariantChange}
            handleReset={handleResetAll}
            handleSave={handleSaveAll}
            isDirty={isDirty}
            loading={loading}
            saveError={saveError}
            saveStatus={saveStatus}
            previewUrl={previewUrl}
            savingPendingAssets={savingPendingAssets}
          />
          <ThemeSetterRightPanel
            normalizedCustomer={normalizedCustomer}
            previewUrl={previewUrl}
            iframeRef={iframeRef}
            onPreviewLoad={handlePreviewLoad}
          />
        </div>
      </div>
    </div>
  );
};
